/**
 * readiness.js — the ONE client source for lesson/practice/test readiness (Lesson Integrity Audit).
 * Reads the stored audit map (`/api/integrity/map`), never infers readiness from lesson presence.
 * Offline/unavailable ⇒ `null` map: callers keep their existing behaviour so nothing disappears due to network.
 */
import { useEffect, useState } from "react";

const KEY = "mwq_readiness_map_v1";
const TTL = 60 * 60 * 1000;
let cache = null;
let inflight = null;

const TIER_OF = { seedlings: "seedling", explorers: "explorer", pioneers: "pioneer", "5-7": "seedling", "8-10": "explorer", "11-15": "pioneer", seedling: "seedling", explorer: "explorer", pioneer: "pioneer" };
export const backendTier = (t) => TIER_OF[t] || "explorer";

export async function fetchReadinessMap() {
  if (cache) return cache;
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || "null");
    if (c && Date.now() - c.at < TTL && c.map?.items) { cache = c.map; return cache; }
  } catch { /* ignore */ }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrity/map`);
      if (!r.ok) throw new Error(String(r.status));
      const map = await r.json();
      if (!map?.count) return null;
      cache = map;
      try { localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), map })); } catch { /* ignore */ }
      return cache;
    } catch {
      try { const c = JSON.parse(localStorage.getItem(KEY) || "null"); return c?.map || null; } catch { return null; }
    } finally { inflight = null; }
  })();
  return inflight;
}

/** Readiness for one code at a tier. `known` is false when the audit has no record (fall back to legacy behaviour). */
export function readinessFor(map, code, tier) {
  const t = backendTier(tier);
  const it = map?.items?.[code];
  if (!it) return { known: false, lesson: null, practice: null, test: null, anyReady: null, status: null };
  return {
    known: true,
    lesson: it.l.length > 0,
    practice: it.p.includes(t),
    test: it.t.includes(t),
    practiceAnyTier: it.p.length > 0,
    questionCount: it.pc?.[t] ?? 0,
    anyReady: it.s !== "NONE",
    status: it.s,
  };
}

export function useReadiness() {
  const [map, setMap] = useState(cache);
  useEffect(() => { let on = true; fetchReadinessMap().then((m) => { if (on) setMap(m); }); return () => { on = false; }; }, []);
  return { map, loaded: map !== null && map !== undefined, get: (code, tier) => readinessFor(map, code, tier) };
}
